/**
 * Staff PIN hashing.
 *
 * PBKDF2-SHA256 via WebCrypto — the only KDF available in the Workers runtime
 * without shipping WASM (bcrypt, scrypt and Argon2 are not). The same module
 * runs in the browser, so a terminal can verify a manager's PIN offline against
 * a cached hash.
 *
 * THE ITERATION COUNT IS BOUNDED BY THE 10 ms WORKER CPU LIMIT, and that is a
 * real constraint, not a preference. A four-digit PIN has ~13 bits of entropy;
 * no feasible KDF cost makes that resistant to an unthrottled online attack.
 * So the defence here is rate limiting and lockout (FR-053), and the KDF's job
 * is only to stop a leaked database being trivially reversible. Iterations are
 * stored per staff row so the cost can be raised later without invalidating
 * anyone's existing PIN.
 */

/**
 * MEASURED, not guessed: 60,000 iterations costs 3.00 ms in workerd
 * (tests/worker/auth.test.ts prints the figure on every run and fails above
 * 8 ms). That leaves ~7 ms of the 10 ms budget for routing and a D1 read.
 */
export const DEFAULT_PIN_ITERATIONS = 60_000;

const KEY_LENGTH_BITS = 256;
const SALT_BYTES = 16;

export interface PinHash {
  hash: string;
  salt: string;
  iterations: number;
}

function toBase64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

async function derive(pin: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(pin),
    { name: "PBKDF2" },
    false,
    ["deriveBits"],
  );

  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: salt as BufferSource, iterations, hash: "SHA-256" },
    key,
    KEY_LENGTH_BITS,
  );

  return new Uint8Array(bits);
}

export async function hashPin(
  pin: string,
  iterations: number = DEFAULT_PIN_ITERATIONS,
): Promise<PinHash> {
  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const derived = await derive(pin, salt, iterations);
  return { hash: toBase64(derived), salt: toBase64(salt), iterations };
}

/**
 * Constant-time comparison.
 *
 * A length-then-content comparison leaks how much of a hash matched via timing.
 * The window is small over a network, but this costs nothing to do correctly.
 */
function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let difference = 0;
  for (let i = 0; i < a.length; i++) difference |= a[i]! ^ b[i]!;
  return difference === 0;
}

export async function verifyPin(pin: string, stored: PinHash): Promise<boolean> {
  const derived = await derive(pin, fromBase64(stored.salt), stored.iterations);
  return timingSafeEqual(derived, fromBase64(stored.hash));
}

/**
 * PIN format policy.
 *
 * Deliberately permissive on length and strict on triviality: a 4-digit PIN is
 * what staff will actually use behind a counter, and forcing something longer
 * produces a sticky note on the monitor rather than better security.
 */
const TRIVIAL_PINS = new Set([
  "0000", "1111", "2222", "3333", "4444", "5555", "6666", "7777", "8888", "9999",
  "1234", "4321", "0123", "1212", "123456", "654321", "111111", "000000",
]);

export interface PinValidation {
  ok: boolean;
  reason?: string;
}

export function validatePinFormat(pin: string): PinValidation {
  if (!/^\d+$/.test(pin)) return { ok: false, reason: "PIN must be digits only" };
  if (pin.length < 4) return { ok: false, reason: "PIN must be at least 4 digits" };
  if (pin.length > 8) return { ok: false, reason: "PIN must be at most 8 digits" };
  if (TRIVIAL_PINS.has(pin)) return { ok: false, reason: "PIN is too easily guessed" };
  return { ok: true };
}

// ---------------------------------------------------------------------------
// Lockout (FR-053) — the actual defence
// ---------------------------------------------------------------------------

export const MAX_FAILED_ATTEMPTS = 5;
export const LOCKOUT_SECONDS = 300;

export function isLockedOut(lockedUntil: string | null, now: Date = new Date()): boolean {
  if (!lockedUntil) return false;
  return new Date(lockedUntil).getTime() > now.getTime();
}

export function nextLockout(
  failedAttempts: number,
  now: Date = new Date(),
): { failedAttempts: number; lockedUntil: string | null } {
  const attempts = failedAttempts + 1;
  if (attempts < MAX_FAILED_ATTEMPTS) {
    return { failedAttempts: attempts, lockedUntil: null };
  }
  return {
    failedAttempts: attempts,
    lockedUntil: new Date(now.getTime() + LOCKOUT_SECONDS * 1000).toISOString(),
  };
}
